<template>
    <v-container grid-list-xl fluid>
        <v-layout row wrap>
            <v-flex lg12>
                <!-- Section 1: Thông tin chung -->
                <v-card class="mb-3">
                    <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left small>info</v-icon>
                        1. Thông tin chung
                        <v-spacer></v-spacer>
                        <v-btn
                            v-if="canMarkCustomsReady"
                            small
                            color="primary"
                            class="mr-2"
                            @click="markCustomsReady"
                        >
                            <v-icon left small>gavel</v-icon>
                            Chuyển sang Chờ thông quan
                        </v-btn>
                        <v-chip small :color="getStatusColor(order.Status)" dark v-if="order.ID">
                            {{ getStatusName(order.Status) }}
                        </v-chip>
                    </v-card-title>
                    <v-card-text>
                        <v-layout row wrap>
                            <v-flex lg4 sm4 xs12>
                                <v-text-field label="Mã đơn hàng" v-model="order.OrderCode" readonly
                                    disabled outlined dense></v-text-field>
                            </v-flex>
                            <v-flex lg2 sm2 xs12>
                                <v-autocomplete label="Khách hàng *" v-model="order.CustomerID" :items="customers"
                                    item-text="display" item-value="ID" :search-input.sync="customerSearch"
                                    @change="onCustomerChange" clearable :disabled="isOrderLocked" outlined dense></v-autocomplete>
                            </v-flex>
                            <v-flex lg3 sm3 xs12>
                                <v-text-field label="Tên khách" v-model="order.CustomerName" readonly outlined dense></v-text-field>
                            </v-flex>
                            <v-flex lg3 sm3 xs12>
                                <v-autocomplete label="Sale phụ trách" v-model="order.SaleID" :items="staffList"
                                    item-text="FullName" item-value="ID" clearable :disabled="isOrderLocked" outlined dense></v-autocomplete>
                            </v-flex>

                            <v-flex lg4 sm4 xs12>
                                <v-text-field label="Tên đơn vị xuất hóa đơn"
                                    v-model="order.CompanyName" :disabled="isOrderLocked" outlined dense></v-text-field>
                            </v-flex>
                            <v-flex lg2 sm2 xs12>
                                <v-text-field label="Mã số thuế" v-model="order.TaxCode" :disabled="isOrderLocked" outlined dense></v-text-field>
                            </v-flex>
                            <v-flex lg6 sm6 xs12>
                                <v-text-field label="Địa chỉ" v-model="order.Address" :disabled="isOrderLocked" outlined dense></v-text-field>
                            </v-flex>

                            <v-flex lg4 sm4 xs12>
                                <v-select label="Hình thức VC" v-model="order.TransportType" :items="transportTypes"
                                    item-text="name" item-value="value" :disabled="isOrderLocked" outlined dense></v-select>
                            </v-flex>
                            <v-flex lg4 sm4 xs12>
                                <v-autocomplete label="Tuyến đường (Dịch vụ vận chuyển)"
                                    v-model="order.RouteServiceCode" :items="routeList" item-text="ServiceName"
                                    item-value="ServiceCode" clearable @change="onRouteChange" :disabled="isOrderLocked" outlined dense></v-autocomplete>
                            </v-flex>
                            <v-flex lg4 sm4 xs12>
                                <v-text-field label="DHD Code" v-model="order.DhdCode" :disabled="isOrderLocked" outlined dense></v-text-field>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>

                <!-- Section 2: Hàng hóa (Products) -->
                <v-card class="mb-3">
                    <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left small>inventory_2</v-icon>
                        2. Hàng hóa
                        <v-spacer></v-spacer>
                        <v-btn small color="green" dark class="mr-2" @click="exportProducts()" v-if="order.ID && products.length > 0">
                            <v-icon left small>download</v-icon> Xuất Excel
                        </v-btn>
                        <v-btn small color="teal" dark class="mr-2" @click="openImportDialog()" :disabled="isOrderLocked || !order.ID">
                            <v-icon left small>upload</v-icon> Import Excel
                        </v-btn>
                        <v-btn small color="primary" @click="openProductDialog()" :disabled="isOrderLocked">
                            <v-icon left small>add</v-icon> Thêm hàng
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <v-data-table :headers="productHeaders" :items="products" hide-default-footer
                            :items-per-page="-1" :mobile-breakpoint="100" dense>
                            <template v-slot:item="{ item, index }">
                                <tr>
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ item.ProductNameVN }}</td>
                                    <td>{{ item.ProductNameEN }}</td>
                                    <td><code>{{ item.HsCode }}</code></td>
                                    <td>{{ item.Brand }}</td>
                                    <td>{{ item.Model }}</td>
                                    <td>{{ item.Unit }}</td>
                                    <td class="text-right">{{ item.Quantity }}</td>
                                    <td class="text-right">{{ item.PriceVND | currency('', 2) }}</td>
                                    <td class="text-center">
                                        <v-btn icon x-small :disabled="isOrderLocked" @click.stop="openProductDialog(item)">
                                            <v-icon small>edit</v-icon>
                                        </v-btn>
                                        <v-btn icon x-small :disabled="isOrderLocked" @click.stop="removeProduct(item.ID)">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                            <template v-slot:no-data>
                                <div class="text-center grey--text pa-3">Chưa có hàng hóa</div>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>

                <!-- Section 3: Đơn giá (chi phí thuế theo sản phẩm) -->
                <v-card class="mb-3" v-if="products.length > 0">
                    <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left small>attach_money</v-icon>
                        3. Đơn giá
                    </v-card-title>
                    <v-card-text class="pa-0">
                        <div style="overflow-x: auto;">
                            <v-simple-table dense class="mb-1">
                                <thead>
                                    <tr class="grey lighten-4">
                                        <th>Tên hàng</th>
                                        <th class="text-right">Số lượng</th>
                                        <th class="text-right">Đơn giá (USD)</th>
                                        <th class="text-right" style="min-width:120px">Tỷ giá tính thuế</th>
                                        <th class="text-right">Trị giá HĐ (USD)</th>
                                        <th class="text-right">Trị giá tính thuế (VND)</th>
                                        <th class="text-right">Đơn giá tính thuế (VND)</th>
                                        <th class="text-right" style="min-width:100px">Thuế NK (%)</th>
                                        <th class="text-right">Thuế NK phải nộp</th>
                                        <th class="text-right" style="min-width:100px">Thuế GTGT (%)</th>
                                        <th class="text-right">Thuế GTGT phải nộp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in products" :key="item.ID">
                                        <td>{{ item.ProductNameVN }}</td>
                                        <td class="text-right">{{ item.Quantity }}</td>
                                        <td class="text-right">{{ calcPriceValues(item).unitUsd | currency('', 2) }}</td>
                                        <td>
                                            <h-currency-input v-model="item.ExchangeRateUSD" :decimal="0" dense hide-details
                                                single-line class="input-sm" :disabled="isOrderLocked"
                                                @on_blur="updateProductPrice(item)"
                                                @press_enter="updateProductPrice(item)"></h-currency-input>
                                        </td>
                                        <td class="text-right">{{ calcPriceValues(item).A | currency('', 2) }}</td>
                                        <td class="text-right">{{ calcPriceValues(item).B1 | currency('', 0) }}</td>
                                        <td class="text-right font-weight-medium">{{ calcPriceValues(item).V1 | currency('', 0) }}</td>
                                        <td>
                                            <v-text-field v-model.number="item.ImportTaxRate" type="number" dense
                                                hide-details single-line class="input-sm" suffix="%" :disabled="isOrderLocked"
                                                @change="updateProductPrice(item)"></v-text-field>
                                        </td>
                                        <td class="text-right red--text">{{ calcPriceValues(item).S | currency('', 0) }}</td>
                                        <td>
                                            <v-text-field v-model.number="item.VatRate" type="number" dense hide-details
                                                single-line class="input-sm" suffix="%" :disabled="isOrderLocked"
                                                @change="updateProductPrice(item)"></v-text-field>
                                        </td>
                                        <td class="text-right red--text">{{ calcPriceValues(item).G1 | currency('', 0) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr class="grey lighten-4 font-weight-bold">
                                        <td colspan="5" class="text-right">Tổng chi phí thuế:</td>
                                        <td class="text-right">{{ totalPriceB1 | currency('', 0) }}</td>
                                        <td></td>
                                        <td></td>
                                        <td class="text-right red--text">{{ totalPriceS | currency('', 0) }}</td>
                                        <td></td>
                                        <td class="text-right red--text">{{ totalPriceG1 | currency('', 0) }}</td>
                                    </tr>
                                </tfoot>
                            </v-simple-table>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Section 4: Thông tin vận đơn (Waybills) -->
                <v-card class="mb-3">
                    <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left small>local_shipping</v-icon>
                        4. Thông tin vận đơn
                        <v-spacer></v-spacer>
                        <v-btn small color="primary" @click="addNewWaybillRow()" :disabled="isOrderLocked">
                            <v-icon left small>add</v-icon> Thêm vận đơn
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <v-data-table :headers="waybillHeaders" :items="displayWaybills" hide-default-footer
                            :items-per-page="-1" :mobile-breakpoint="100" dense>
                            <template v-slot:item="{ item, index }">
                                <tr v-if="editingWaybillId === item.ID">
                                    <td>{{ item.ID === 'new' ? '+' : index + 1 }}</td>
                                    <td>
                                        <v-text-field v-model="localWaybill.WaybillCode" dense hide-details
                                            single-line class="input-sm" :placeholder="item.ID === 'new' ? 'Mã vận đơn' : ''"></v-text-field>
                                    </td>
                                    <td>
                                        <div class="d-flex align-center">
                                            <v-text-field v-model.number="localWaybill.Length" type="number" dense hide-details
                                                single-line class="input-sm mx-1" placeholder="Dài" @input="calcLocalWaybillVolume"></v-text-field>
                                            <v-text-field v-model.number="localWaybill.Width" type="number" dense hide-details
                                                single-line class="input-sm mx-1" placeholder="Rộng" @input="calcLocalWaybillVolume"></v-text-field>
                                            <v-text-field v-model.number="localWaybill.Height" type="number" dense hide-details
                                                single-line class="input-sm mx-1" placeholder="Cao" @input="calcLocalWaybillVolume"></v-text-field>
                                        </div>
                                    </td>
                                    <td>
                                        <v-text-field v-model.number="localWaybill.PackageCount" type="number" dense hide-details
                                            single-line class="input-sm" @input="calcLocalWaybillVolume"></v-text-field>
                                    </td>
                                    <td>
                                        <v-text-field v-model.number="localWaybill.TotalWeight" type="number" dense hide-details
                                            single-line class="input-sm" @input="calcLocalWaybillVolume"></v-text-field>
                                    </td>
                                    <td class="text-right">{{ localWaybill.TotalVolume }}</td>
                                    <td class="text-right">{{ localWaybill.KGM }}</td>
                                    <td>
                                        <v-text-field v-model="localWaybill.Note" dense hide-details
                                            single-line class="input-sm"></v-text-field>
                                    </td>
                                    <td class="text-center">
                                        <v-btn icon x-small color="success" @click="saveWaybillRow()">
                                            <v-icon>save</v-icon>
                                        </v-btn>
                                        <v-btn icon x-small color="error" @click="cancelWaybillEdit()">
                                            <v-icon>cancel</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td>{{ index + 1 }}</td>
                                    <td><code>{{ item.WaybillCode }}</code></td>
                                    <td class="text-right">{{ item.Length }} × {{ item.Width }} × {{ item.Height }}</td>
                                    <td class="text-right">{{ item.PackageCount }}</td>
                                    <td class="text-right">{{ item.TotalWeight }}</td>
                                    <td class="text-right">{{ item.TotalVolume }}</td>
                                    <td class="text-right font-weight-medium">{{ item.KGM }}</td>
                                    <td>{{ item.Note }}</td>
                                    <td class="text-center">
                                        <v-btn icon x-small :disabled="isOrderLocked" @click.stop="editWaybillRow(item)">
                                            <v-icon small>edit</v-icon>
                                        </v-btn>
                                        <v-btn icon x-small :disabled="isOrderLocked" @click.stop="removeWaybill(item.ID)">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                            <template v-slot:body.append v-if="waybills.length > 0">
                                <tr class="grey lighten-4 font-weight-bold">
                                    <td colspan="3" class="text-right">Tổng:</td>
                                    <td class="text-right">{{ totalWaybillPacks }}</td>
                                    <td class="text-right">{{ totalWaybillWeight }}</td>
                                    <td class="text-right">{{ totalWaybillVolume }}</td>
                                    <td class="text-right">{{ totalWaybillKGM }}</td>
                                    <td colspan="2"></td>
                                </tr>
                            </template>
                            <template v-slot:no-data>
                                <div class="text-center grey--text pa-3">Chưa có vận đơn</div>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>

                <!-- Section 5: Dịch vụ và phải thu khách hàng -->
                <v-card class="mb-3">
                    <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left small>receipt_long</v-icon>
                        5. Dịch vụ và phải thu khách hàng
                        <v-spacer></v-spacer>
                        <v-btn small color="primary" @click="openFeeDialog()" :disabled="isOrderLocked">
                            <v-icon left small>add</v-icon> Thêm phí
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <!-- Group I: Hóa đơn -->
                        <div class="subtitle-2 font-weight-bold mb-1 mt-2 blue--text">(I) Hóa đơn</div>
                        <v-simple-table dense class="mb-3 elevation-1">
                            <thead>
                                <tr class="blue lighten-5">
                                    <th>Tên hàng</th>
                                    <th class="text-right">Số lượng</th>
                                    <th class="text-right">Đơn giá vốn</th>
                                    <th class="text-right" style="width: 160px;">Đơn giá hóa đơn</th>
                                    <th class="text-right">Trị giá hóa đơn</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="products.length === 0">
                                    <td colspan="5" class="text-center grey--text pa-3">Chưa có hàng hóa</td>
                                </tr>
                                <tr v-for="item in products" :key="item.ID">
                                    <td>{{ item.ProductNameVN }}</td>
                                    <td class="text-right">{{ item.Quantity }}</td>
                                    <td class="text-right">{{ item.PriceC_VND | currency('', 0) }}</td>
                                    <td>
                                        <h-currency-input v-model="item.InvoiceUnitPriceVND" :decimal="0" dense hide-details
                                            single-line class="input-sm" :disabled="isOrderLocked"
                                            @on_blur="updateInvoicePrice(item)"
                                            @press_enter="updateInvoicePrice(item)"></h-currency-input>
                                    </td>
                                    <td class="text-right font-weight-medium">{{ item.InvoiceAmountVND | currency('', 0) }}</td>
                                </tr>
                            </tbody>
                            <tfoot v-if="products.length > 0">
                                <tr class="blue lighten-5 font-weight-bold">
                                    <td colspan="4" class="text-right">Tổng (I):</td>
                                    <td class="text-right">{{ order.TotalInvoiceAmount | currency('', 0) }}</td>
                                </tr>
                            </tfoot>
                        </v-simple-table>

                        <!-- Group II: Phí ủy thác -->
                        <div class="subtitle-2 font-weight-bold mb-1 mt-3 orange--text">(II) Phí ủy thác</div>
                        <v-simple-table dense class="mb-3 elevation-1">
                            <thead>
                                <tr class="orange lighten-5">
                                    <th>Tên hàng</th>
                                    <th class="text-right">Trị giá hóa đơn</th>
                                    <th class="text-right" style="width:120px">Phí ủy thác (%)</th>
                                    <th class="text-right">Phải thu</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="products.length === 0">
                                    <td colspan="6" class="text-center grey--text pa-3">Chưa có hàng hóa</td>
                                </tr>
                                <tr v-for="row in products" :key="row.ID">
                                    <td>{{ row.ProductNameVN }}</td>
                                    <td class="text-right font-weight-medium">{{ row.InvoiceAmountVND | currency('', 0) }}</td>
                                    <td>
                                        <v-text-field
                                            v-model.number="trustFeeRates[row.ID]"
                                            type="number" min="0" max="100" step="0.1"
                                            dense hide-details single-line suffix="%"
                                            class="input-sm" :disabled="isOrderLocked"
                                            @change="updateTrustFeeRate(row)"
                                        ></v-text-field>
                                    </td>
                                    <td class="text-right font-weight-medium orange--text">{{ row.TrustFeeAmount | currency('', 0) }}</td>
                                </tr>
                            </tbody>
                            <tfoot v-if="products.length > 0">
                                <tr class="orange lighten-5 font-weight-bold">
                                    <td colspan="3" class="text-right">Tổng (II):</td>
                                    <td class="text-right">{{ order.TotalTrustFee | currency('', 0) }}</td>
                                </tr>
                            </tfoot>
                        </v-simple-table>

                        <!-- Group III: Thuế -->
                        <div class="subtitle-2 font-weight-bold mb-1 mt-3 red--text">(III) Thuế</div>
                        <v-simple-table dense class="mb-3 elevation-1">
                            <thead>
                                <tr class="red lighten-5">
                                    <th>Tên hàng</th>
                                    <th class="text-right">Thuế nhập khẩu</th>
                                    <th class="text-right">Trị giá hóa đơn</th>
                                    <th class="text-right">%GTGT</th>
                                    <th class="text-right">Thuế GTGT</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="products.length === 0">
                                    <td colspan="5" class="text-center grey--text pa-3">Chưa có hàng hóa</td>
                                </tr>
                                <tr v-for="item in products" :key="item.ID">
                                    <td>{{ item.ProductNameVN }}</td>
                                    <td class="text-right red--text">{{ item.ImportTaxAmount | currency('', 0) }}</td>
                                    <td class="text-right">{{ item.InvoiceAmountVND | currency('', 0) }}</td>
                                    <td class="text-right">{{ Number(item.VatRate || 0).toFixed(2) }}%</td>
                                    <td class="text-right font-weight-medium red--text">{{ item.VatOutputAmount | currency('', 0) }}</td>
                                </tr>
                            </tbody>
                            <tfoot v-if="products.length > 0">
                                <tr class="red lighten-5 font-weight-bold">
                                    <td class="text-right">Tổng thuế nhập khẩu:</td>
                                    <td class="text-right red--text">{{ order.TotalImportTax | currency('', 0) }}</td>
                                    <td colspan="3"></td>
                                </tr>
                                <tr class="red lighten-5 font-weight-bold">
                                    <td colspan="4" class="text-right">Tổng thuế GTGT:</td>
                                    <td class="text-right red--text">{{ order.TotalVatOutput | currency('', 0) }}</td>
                                </tr>
                                <tr class="red lighten-4 font-weight-bold">
                                    <td colspan="4" class="text-right">Tổng (III):</td>
                                    <td class="text-right red--text">{{ (order.TotalImportTax + order.TotalVatOutput) | currency('', 0) }}</td>
                                </tr>
                            </tfoot>
                        </v-simple-table>

                        <!-- Group IV: Dịch vụ DHD -->
                        <div class="subtitle-2 font-weight-bold mb-1 mt-3 purple--text">(IV) Dịch vụ DHD</div>
                        <v-data-table :headers="feeHeaders" :items="serviceFees" hide-default-footer
                            :items-per-page="-1" :mobile-breakpoint="100" dense class="elevation-1">
                            <template v-slot:item="{ item }">
                                <tr>
                                    <td>{{ item.ServiceName }}</td>
                                    <td v-if="showCost" class="text-right">{{ item.CostPrice | currency('', 0) }}</td>
                                    <td class="text-right">{{ item.SellPrice | currency('', 0) }}</td>
                                    <td>{{ item.Unit }}</td>
                                    <td class="text-right">{{ item.Quantity }}</td>
                                    <td class="text-right font-weight-medium">{{ item.Amount | currency('', 0) }}</td>
                                    <td class="text-center">
                                        <v-btn icon x-small :disabled="isOrderLocked" @click.stop="openFeeDialog(item)">
                                            <v-icon small>edit</v-icon>
                                        </v-btn>
                                        <v-btn icon x-small :disabled="isOrderLocked" @click.stop="removeFee(item.ID)">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                            <template v-slot:body.append v-if="serviceFees.length > 0">
                                <tr class="purple lighten-5 font-weight-bold">
                                    <td :colspan="showCost ? 5 : 4" class="text-right">Tổng (IV):</td>
                                    <td class="text-right">{{ order.TotalServiceFee | currency('', 0) }}</td>
                                    <td></td>
                                </tr>
                            </template>
                        </v-data-table>

                        <!-- Cước vận chuyển xuyên biên giới (hiển thị riêng nếu có) -->
                        <div v-if="transportFees && transportFees.length > 0">
                            <div class="subtitle-2 font-weight-bold mb-1 mt-3 teal--text">Cước vận chuyển xuyên biên giới</div>
                            <v-data-table :headers="feeHeaders" :items="transportFees" hide-default-footer
                                :items-per-page="-1" :mobile-breakpoint="100" dense class="elevation-1">
                                <template v-slot:item="{ item }">
                                    <tr>
                                        <td>{{ item.ServiceName }}</td>
                                        <td v-if="showCost" class="text-right">{{ item.CostPrice | currency('', 0) }}</td>
                                        <td class="text-right">{{ item.SellPrice | currency('', 0) }}</td>
                                        <td>{{ item.Unit }}</td>
                                        <td class="text-right">{{ item.Quantity }}</td>
                                        <td class="text-right font-weight-medium">{{ item.Amount | currency('', 0) }}</td>
                                        <td class="text-center">
                                            <v-btn icon x-small :disabled="isOrderLocked" @click.stop="openFeeDialog(item)">
                                                <v-icon small>edit</v-icon>
                                            </v-btn>
                                            <v-btn icon x-small :disabled="isOrderLocked" @click.stop="removeFee(item.ID)">
                                                <v-icon small>delete</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                </template>
                                <template v-slot:body.append>
                                    <tr class="teal lighten-5 font-weight-bold">
                                        <td :colspan="showCost ? 5 : 4" class="text-right">Tổng vận chuyển:</td>
                                        <td class="text-right">{{ order.TotalTransportFee | currency('', 0) }}</td>
                                        <td></td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </div>

                        <!-- Tổng phải thu -->
                        <v-divider class="mt-3 mb-2"></v-divider>
                        <v-layout row>
                            <v-spacer></v-spacer>
                            <div class="subtitle-1 font-weight-bold mr-4 text-right">
                                <div class="grey--text text--darken-1" style="font-size:13px; font-weight:normal">
                                    (I) Hóa đơn: {{ order.TotalInvoiceAmount | currency('', 0) }} &nbsp;+&nbsp;
                                    (II) Ủy thác: {{ order.TotalTrustFee | currency('', 0) }} &nbsp;+&nbsp;
                                    (III) Thuế: {{ (order.TotalImportTax + order.TotalVatOutput) | currency('', 0) }} &nbsp;+&nbsp;
                                    (IV) Dịch vụ: {{ order.TotalServiceFee | currency('', 0) }} &nbsp;+&nbsp;
                                    Vận chuyển: {{ order.TotalTransportFee | currency('', 0) }} &nbsp;=&nbsp;
                                </div>
                                <div class="title mt-1">
                                    TỔNG PHẢI THU: <span class="blue--text font-weight-bold">{{ order.TotalReceivable | currency('', 0) }} VND</span>
                                </div>
                            </div>
                        </v-layout>
                    </v-card-text>
                </v-card>

                <!-- Section 6: Cân đối tài chính -->
                <v-card class="mb-3">
                    <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left small>account_balance_wallet</v-icon>
                        6. Cân đối tài chính
                    </v-card-title>
                    <v-card-text>
                        <!-- 6A. Bảng các khoản đã thu -->
                        <div class="d-flex align-center mb-2">
                            <div class="subtitle-2 font-weight-bold">Các khoản tiền đã thu của khách hàng (Ghi có)</div>
                            <v-spacer></v-spacer>
                            <v-btn small color="primary" @click="openCollectionDialog()" :disabled="isOrderLocked">
                                <v-icon left small>add</v-icon> Thêm khoản thu
                            </v-btn>
                        </div>
                        <v-data-table :headers="collectionHeaders" :items="collections" hide-default-footer
                            :items-per-page="-1" :mobile-breakpoint="100" dense class="mb-4 elevation-1">
                            <template v-slot:item="{ item }">
                                <tr>
                                    <td>{{ item.ReceiverName }}</td>
                                    <td>{{ item.Note }}</td>
                                    <td class="text-right font-weight-medium">{{ item.Amount | currency('', 0) }}</td>
                                    <td class="text-center">
                                        <v-btn icon x-small :disabled="isOrderLocked" @click.stop="openCollectionDialog(item)">
                                            <v-icon small>edit</v-icon>
                                        </v-btn>
                                        <v-btn icon x-small :disabled="isOrderLocked" v-if="adminRole || accountantRole" @click.stop="removeCollection(item.ID)">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                            <template v-slot:body.append v-if="collections.length > 0">
                                <tr class="grey lighten-4 font-weight-bold">
                                    <td colspan="2">Tổng đã thu (Ghi có)</td>
                                    <td class="text-right">{{ totalCollections | currency('', 0) }}</td>
                                    <td></td>
                                </tr>
                            </template>
                            <template v-slot:no-data>
                                <div class="text-center grey--text pa-3">Chưa có khoản thu nào</div>
                            </template>
                        </v-data-table>

                        <!-- 6B. Bảng cân đối tài chính dạng T-account -->
                        <div class="subtitle-2 font-weight-bold mb-2">Bảng tổng hợp cân đối tài chính đơn hàng</div>
                        <div v-if="order.financial_balance" class="t-account-wrapper elevation-1">
                            <table class="t-account-table">
                                <thead>
                                    <!-- Row 1: tiêu đề cân đối -->
                                    <tr>
                                        <th colspan="4" class="t-account-header">
                                            <span class="font-weight-bold" :class="order.financial_balance.balance < 0 ? 'red--text' : 'green--text'">
                                                <span v-if="order.financial_balance.balance < 0">(-) {{ Math.abs(order.financial_balance.balance) | currency('', 0) }}</span>
                                                <span v-else>{{ order.financial_balance.balance | currency('', 0) }}</span>
                                                VND
                                            </span>
                                        </th>
                                    </tr>
                                    <!-- Row 2: Ghi nợ / Ghi có -->
                                    <tr>
                                        <th colspan="2" class="t-account-side t-account-debit">Ghi nợ</th>
                                        <th colspan="2" class="t-account-side t-account-credit">Ghi có</th>
                                    </tr>
                                    <!-- Row 3: sub-columns -->
                                    <tr>
                                        <th class="t-account-sub">Số tiền</th>
                                        <th class="t-account-sub">Diễn giải</th>
                                        <th class="t-account-sub">Số tiền</th>
                                        <th class="t-account-sub">Diễn giải</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(_, i) in balanceRows" :key="i">
                                        <!-- Ghi nợ -->
                                        <td class="t-account-amount t-account-debit-cell">
                                            <span v-if="order.financial_balance.debit_items[i]">
                                                {{ order.financial_balance.debit_items[i].Amount | currency('', 0) }}
                                            </span>
                                        </td>
                                        <td class="t-account-desc t-account-debit-cell">
                                            <span v-if="order.financial_balance.debit_items[i]">
                                                {{ order.financial_balance.debit_items[i].Source }}
                                            </span>
                                        </td>
                                        <!-- Ghi có -->
                                        <td class="t-account-amount t-account-credit-cell">
                                            <span v-if="order.financial_balance.credit_items[i]">
                                                {{ order.financial_balance.credit_items[i].Amount | currency('', 0) }}
                                            </span>
                                        </td>
                                        <td class="t-account-desc t-account-credit-cell">
                                            <span v-if="order.financial_balance.credit_items[i]">
                                                {{ order.financial_balance.credit_items[i].Source }}
                                            </span>
                                        </td>
                                    </tr>
                                    <!-- Empty row nếu chưa có dữ liệu -->
                                    <tr v-if="balanceRows.length === 0">
                                        <td class="t-account-amount t-account-debit-cell"></td>
                                        <td class="t-account-desc t-account-debit-cell"></td>
                                        <td class="t-account-amount t-account-credit-cell"></td>
                                        <td class="t-account-desc t-account-credit-cell"></td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr class="t-account-total-row">
                                        <td class="t-account-total-amount t-account-debit-cell font-weight-bold">
                                            {{ order.financial_balance.total_debit | currency('', 0) }}
                                        </td>
                                        <td class="t-account-total-label t-account-debit-cell font-weight-bold">
                                            Tổng Ghi nợ
                                        </td>
                                        <td class="t-account-total-amount t-account-credit-cell font-weight-bold">
                                            {{ order.financial_balance.total_credit | currency('', 0) }}
                                        </td>
                                        <td class="t-account-total-label t-account-credit-cell font-weight-bold">
                                            Tổng Ghi có
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Section 7: Lợi nhuận đơn hàng -->
                <v-card class="mb-3" v-if="order.profit_rows && order.profit_rows.length > 0">
                    <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left small>trending_up</v-icon>
                        7. Lợi nhuận đơn hàng
                    </v-card-title>
                    <v-card-text>
                        <v-simple-table dense class="elevation-1">
                            <thead>
                                <tr class="grey lighten-3">
                                    <th>Hạng mục</th>
                                    <th class="text-right">Tổng chi phí (VND)</th>
                                    <th class="text-right">Tổng doanh thu (VND)</th>
                                    <th class="text-right">Lợi nhuận (VND)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in order.profit_rows" :key="idx">
                                    <td>{{ item.Category }}</td>
                                    <td class="text-right">{{ item.Cost | currency('', 0) }}</td>
                                    <td class="text-right">{{ item.Revenue | currency('', 0) }}</td>
                                    <td class="text-right font-weight-medium" :class="item.Profit < 0 ? 'red--text' : 'green--text'">
                                        {{ item.Profit | currency('', 0) }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr class="grey lighten-2 font-weight-bold">
                                    <td>Tổng cộng</td>
                                    <td class="text-right">{{ order.TotalCost | currency('', 0) }}</td>
                                    <td class="text-right">{{ order.TotalReceivable | currency('', 0) }}</td>
                                    <td class="text-right" :class="order.TotalProfit < 0 ? 'red--text' : 'green--text'">
                                        {{ order.TotalProfit | currency('', 0) }}
                                    </td>
                                </tr>
                            </tfoot>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>

        <!-- ===== Product Dialog ===== -->
        <v-dialog v-model="dialogProduct" max-width="1200px" scrollable>
            <v-card>
                <v-card-title class="subtitle-1 font-weight-bold">
                    {{ editingProduct.ID ? 'Sửa hàng hóa' : 'Thêm hàng hóa' }}
                </v-card-title>
                <v-card-text>
                    <v-container grid-list-md pa-0 pt-2>
                        <v-layout row wrap>
                            <!-- Hàng 1: Định danh -->
                            <v-flex lg6><v-text-field label="HS Code *" v-model="editingProduct.HsCode" outlined dense></v-text-field></v-flex>
                            <v-flex lg6><v-text-field label="SN No. / 产品序列号" v-model="editingProduct.SerialNumber" outlined dense></v-text-field></v-flex>

                            <!-- Hàng 2: Tên sản phẩm chính -->
                            <v-flex lg6><v-text-field label="Tên hàng VN / 越南品名 *" v-model="editingProduct.ProductNameVN" outlined dense></v-text-field></v-flex>
                            <v-flex lg6><v-text-field label="Tên hàng EN / 英文品名 *" v-model="editingProduct.ProductNameEN" outlined dense></v-text-field></v-flex>

                            <!-- Hàng 3: Quy cách kỹ thuật -->
                            <v-flex lg3><v-text-field label="Nhãn hiệu / 品牌" v-model="editingProduct.Brand" outlined dense></v-text-field></v-flex>
                            <v-flex lg3><v-text-field label="Model / 型号" v-model="editingProduct.Model" outlined dense></v-text-field></v-flex>
                            <v-flex lg3><v-text-field label="Thành phần / 材质成份" v-model="editingProduct.MaterialComposition" outlined dense></v-text-field></v-flex>
                            <v-flex lg3><v-text-field label="Quy cách / 规格" v-model="editingProduct.Specifications" outlined dense></v-text-field></v-flex>

                            <!-- Hàng 4: Nhà sản xuất & Công dụng -->
                            <v-flex lg6><v-text-field label="Công dụng / 功能用途" v-model="editingProduct.FunctionUse" outlined dense></v-text-field></v-flex>
                            <v-flex lg6><v-text-field label="Nhà sản xuất / 制造商名称" v-model="editingProduct.ManufacturerName" outlined dense></v-text-field></v-flex>

                            <!-- Hàng 5: Kích thước & Xuất xứ -->
                            <v-flex lg3><v-text-field label="Kích thước / 长宽高CM" v-model="editingProduct.ExternalDimension" placeholder="DxRxC" outlined dense></v-text-field></v-flex>
                            <v-flex lg3><h-currency-input label="Số lượng / 数量 *" v-model="editingProduct.Quantity" :decimal="0" outlined dense></h-currency-input></v-flex>
                            <v-flex lg3><v-text-field label="Đơn vị / 计量单位 *" v-model="editingProduct.Unit" placeholder="PCS, KG..." outlined dense></v-text-field></v-flex>
                            <v-flex lg3><v-text-field label="Xuất xứ / 原产国" v-model="editingProduct.CountryOfOrigin" placeholder="China" outlined dense></v-text-field></v-flex>

                            <!-- Hàng 6: Giá & Trọng lượng -->
                            <v-flex lg4><h-currency-input label="NET.W/PCS" v-model="editingProduct.NetWeightPerPiece" :decimal="3" outlined dense></h-currency-input></v-flex>
                            <v-flex lg8><h-currency-input label="Đơn giá (USD)" v-model="editingProduct.PriceVND" :decimal="2" outlined dense></h-currency-input></v-flex>
                            
                            <!-- Hàng 7: Ảnh (Upload) -->
                            <v-flex lg6>
                                <v-file-input 
                                    label="Product Picture / 产品图片 (Upload)" 
                                    v-model="productImageFile" 
                                    accept="image/*"
                                    prepend-icon="mdi-camera"
                                    outlined dense
                                    @change="onProductImageChange"
                                    :loading="uploadingProductImage"
                                ></v-file-input>
                                <div v-if="editingProduct.ProductImage" class="mt-n2 mb-2">
                                    <v-img :src="editingProduct.ProductImage" max-height="100" contain position="left center"></v-img>
                                </div>
                            </v-flex>
                            <v-flex lg6>
                                <v-file-input 
                                    label="Nameplate Picture / 铭牌图片 (Upload)" 
                                    v-model="nameplateImageFile" 
                                    accept="image/*"
                                    prepend-icon="mdi-camera-account"
                                    outlined dense
                                    @change="onNameplateImageChange"
                                    :loading="uploadingNameplateImage"
                                ></v-file-input>
                                <div v-if="editingProduct.NameplateImage" class="mt-n2 mb-2">
                                    <v-img :src="editingProduct.NameplateImage" max-height="100" contain position="left center"></v-img>
                                </div>
                            </v-flex>
                        </v-layout>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red darken-1 white--text" @click="dialogProduct = false">Đóng</v-btn>
                    <v-btn color="primary" @click="saveProduct">Lưu</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ===== Fee Dialog ===== -->
        <v-dialog v-model="dialogFee" max-width="700px">
            <v-card>
                <v-card-title class="subtitle-1 font-weight-bold">
                    {{ editingFee.ID ? 'Sửa phí' : 'Thêm phí' }}
                </v-card-title>
                <v-card-text>
                    <v-container grid-list-md pa-0>
                        <v-layout row wrap>
                            <v-flex lg6>
                                <v-select label="Nhóm phí *" v-model="editingFee.FeeGroup" :items="feeGroups"
                                    item-text="name" item-value="value" outlined dense></v-select>
                            </v-flex>
                            <v-flex lg6>
                                <v-autocomplete label="Dịch vụ (từ danh mục)" v-model="selectedService"
                                    :items="serviceList" item-text="ServiceName" item-value="ID" clearable return-object
                                    @change="onServiceSelect" outlined dense></v-autocomplete>
                            </v-flex>
                            <v-flex lg6><v-text-field label="Tên dịch vụ"
                                    v-model="editingFee.ServiceName" outlined dense></v-text-field></v-flex>
                            <v-flex lg6><v-text-field label="Ký hiệu"
                                    v-model="editingFee.ServiceCode" outlined dense></v-text-field></v-flex>
                            <v-flex lg3 v-if="showCost"><h-currency-input label="Giá gốc" v-model="editingFee.CostPrice"
                                    :decimal="0" outlined dense></h-currency-input></v-flex>
                            <v-flex lg3><h-currency-input label="Giá bán" v-model="editingFee.SellPrice"
                                    :decimal="0" outlined dense></h-currency-input></v-flex>
                            <v-flex lg3><v-text-field label="Đơn vị" v-model="editingFee.Unit" outlined dense></v-text-field></v-flex>
                            <v-flex lg3><v-text-field label="Số lượng" v-model.number="editingFee.Quantity"
                                    type="number" outlined dense></v-text-field></v-flex>
                            <v-flex lg3 v-if="editingFee.FeeGroup === 'TAX'"><v-text-field label="% Thuế"
                                    v-model.number="editingFee.TaxRate" type="number"
                                    suffix="%" outlined dense></v-text-field></v-flex>
                        </v-layout>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red darken-1 white--text" @click="dialogFee = false">Đóng</v-btn>
                    <v-btn color="primary" @click="saveFee">Lưu</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ===== Collection Dialog ===== -->
        <v-dialog v-model="dialogCollection" max-width="500px">
            <v-card>
                <v-card-title class="subtitle-1 font-weight-bold">
                    {{ editingCollection.ID ? 'Sửa khoản thu' : 'Thêm khoản thu' }}
                </v-card-title>
                <v-card-text>
                    <v-container grid-list-md pa-0 pt-2>
                        <v-layout row wrap>
                            <v-flex lg12>
                                <v-combobox label="Người nhận (ReceiverName)" 
                                    v-model="editingCollection.ReceiverName" 
                                    :items="staffList"
                                    item-text="FullName"
                                    item-value="FullName"
                                    :return-object="false"
                                    outlined dense></v-combobox>
                            </v-flex>
                            <v-flex lg12>
                                <v-text-field label="Ghi chú *" 
                                    v-model="editingCollection.Note" outlined dense></v-text-field>
                            </v-flex>
                            <v-flex lg12>
                                <h-currency-input label="Số tiền (VND) *" 
                                    v-model="editingCollection.Amount" :decimal="0" outlined dense></h-currency-input>
                            </v-flex>
                        </v-layout>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red darken-1 white--text" @click="dialogCollection = false">Đóng</v-btn>
                    <v-btn color="primary" @click="saveCollection">Lưu</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ===== Import Excel Dialog ===== -->
        <v-dialog v-model="dialogImport" max-width="560px">
            <v-card>
                <v-card-title class="subtitle-1 font-weight-bold py-3 px-5">
                    <v-icon left color="primary">file_upload</v-icon>
                    Import hàng hóa CN từ Excel
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4 px-5 pb-2">
                    <v-file-input
                        label="File Excel (.xlsx, .xls) *"
                        accept=".xlsx,.xls"
                        v-model="importForm.file"
                        @change="onImportFileChange"
                        outlined dense
                        prepend-icon=""
                        prepend-inner-icon="table_chart"
                        class="mb-2"
                    ></v-file-input>
                    <v-alert v-if="excelHeaders.length > 0" dense text type="success" class="mb-0" icon="check_circle">
                        Đọc được <strong>{{ excelRows.length }}</strong> dòng dữ liệu.
                        <span v-if="mappingAutoApplied"> Đã áp dụng mapping cột đã lưu.</span>
                    </v-alert>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="px-5 py-3">
                    <v-btn text small color="grey darken-1" :disabled="!excelHeaders.length" @click="openMappingDialog">
                        <v-icon small left>tune</v-icon> Match cột
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text color="grey darken-1" @click="closeImportDialog">Đóng</v-btn>
                    <v-btn color="primary" :loading="importing" :disabled="!excelHeaders.length" @click="startImport">
                        <v-icon left small>file_upload</v-icon> Import
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ===== Column Mapping Dialog ===== -->
        <v-dialog v-model="dialogColumnMapping" max-width="700px" persistent>
            <v-card>
                <v-card-title class="subtitle-1 font-weight-bold py-3 px-5">
                    <v-icon left color="primary">tune</v-icon>
                    Match cột dữ liệu Excel
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="px-5 pt-4 pb-1">
                    <v-alert dense text type="info" class="mb-4" icon="info">
                        Các cột có dấu <strong class="red--text">*</strong> là bắt buộc. Để trống các cột không có trong file.
                    </v-alert>
                    <v-row dense>
                        <v-col v-for="field in mappingFields" :key="field.key" cols="12" sm="6">
                            <v-select
                                :label="`${field.label}${field.required ? ' *' : ''}`"
                                v-model="columnMapping[field.key]"
                                :items="excelHeaders"
                                clearable outlined dense
                            ></v-select>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider class="mt-3"></v-divider>
                <v-card-actions class="px-5 py-3">
                    <v-spacer></v-spacer>
                    <v-btn text color="grey darken-1" @click="dialogColumnMapping = false">Hủy</v-btn>
                    <v-btn color="primary" @click="confirmColumnMapping">
                        <v-icon left small>save</v-icon> Lưu mapping
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- System Logs Section -->
        <v-card class="mt-3" v-if="order.ID">
            <v-card-title class="subtitle-1 font-weight-bold">
                <v-icon left small>history</v-icon>
                Nhật ký chỉnh sửa
            </v-card-title>
            <v-card-text>
                <v-data-table :headers="log_headers" :items="systemLogs" :loading="loadingLogs" disable-pagination
                    hide-default-footer dense>
                    <template v-slot:item.created_at="{ item }">
                        {{ item.created_at | display_date }}
                    </template>
                    <template v-slot:item.content="{ item }">
                        <div style="max-width: 600px; white-space: pre-wrap; font-size: 12px;">
                            <span v-html="item.content"></span>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import configs from "@/configs";
import _ from "lodash";
import fileUploadService from "@/services/fileUploadService";
import { sendErrorNotice, sendSuccessNotice } from "@/commons/utils";
import * as XLSX from 'xlsx';
import ApiService from '@/services/api.service';

const IMPORT_MAPPING_STORAGE_KEY = 'cn_product_import_mapping_v1'

export default {
    data() {
        return {
            transportTypes: configs.CN_TRANSPORT_TYPE,
            statusList: configs.CN_ORDER_STATUS_LIST,
            feeGroups: configs.CN_FEE_GROUP,
            customerSearch: '',
            customers: [],
            staffList: [],
            serviceList: [],
            selectedService: null,
            // Dialogs
            dialogProduct: false,
            dialogFee: false,
            dialogCollection: false,
            editingProduct: {},
            editingWaybill: {},
            editingWaybillId: null,
            localWaybill: {},
            editingFee: {},
            editingCollection: {},
            // Image upload states
            productImageFile: null,
            nameplateImageFile: null,
            uploadingProductImage: false,
            uploadingNameplateImage: false,
            // Table headers
            productHeaders: [
                { text: '#', sortable: false, width: 40 },
                { text: 'Tên hàng VN / 越南品名', value: 'ProductNameVN' },
                { text: 'Tên hàng EN / 英文品名', value: 'ProductNameEN' },
                { text: 'HS Code', value: 'HsCode' },
                { text: 'Nhãn hiệu / 品牌', value: 'Brand' },
                { text: 'Model / 型号', value: 'Model' },
                { text: 'Đơn vị / 计量单位', value: 'Unit' },
                { text: 'Số lượng / 数量', value: 'Quantity', align: 'right' },
                { text: 'Đơn giá (USD)', value: 'PriceVND', align: 'right' },
                { text: '', sortable: false, width: 80 }
            ],
            waybillHeaders: [
                { text: '#', sortable: false, width: 40 },
                { text: 'Mã vận đơn', value: 'WaybillCode' },
                { text: 'D×R×C (cm)', sortable: false, align: 'right', width: 340 },
                { text: 'Kiện', value: 'PackageCount', align: 'right' },
                { text: 'Cân (KG)', value: 'TotalWeight', align: 'right' },
                { text: 'Khối (M3)', value: 'TotalVolume', align: 'right' },
                { text: 'KGM', value: 'KGM', align: 'right' },
                { text: 'Ghi chú', value: 'Note' },
                { text: '', sortable: false, width: 80 }
            ],
            collectionHeaders: [
                { text: 'Người nhận', value: 'ReceiverName' },
                { text: 'Ghi chú', value: 'Note' },
                { text: 'Amount', value: 'Amount', align: 'right' },
                { text: '', sortable: false, width: 80 }
            ],
            trustFeeRates: {},
            systemLogs: [],
            loadingLogs: false,
            // Import Excel
            dialogImport: false,
            dialogColumnMapping: false,
            importing: false,
            importForm: { file: null },
            excelHeaders: [],
            excelRows: [],
            currentHeaderSignature: '',
            mappingAutoApplied: false,
            columnMapping: {},
            mappingFields: [
                { key: 'ProductNameVN', label: 'Tên hàng VN', required: true, aliases: ['tên hàng vn', 'ten hang vn', 'productnamevn'] },
                { key: 'ProductNameEN', label: 'Tên hàng EN', aliases: ['tên hàng en', 'ten hang en', 'productnameen'] },
                { key: 'HsCode', label: 'HS Code', aliases: ['hs code', 'hscode', 'mã hs', 'ma hs'] },
                { key: 'Brand', label: 'Nhãn hiệu', aliases: ['nhãn hiệu', 'nhan hieu', 'brand'] },
                { key: 'Model', label: 'Model', aliases: ['model'] },
                { key: 'Unit', label: 'Đơn vị', aliases: ['đơn vị', 'don vi', 'unit'] },
                { key: 'Quantity', label: 'Số lượng', required: true, number: true, aliases: ['số lượng', 'so luong', 'quantity', 'sl'] },
                { key: 'PriceVND', label: 'Đơn giá (USD)', required: true, number: true, aliases: ['giá usd', 'gia usd', 'pricevnd', 'usd', 'đơn giá', 'don gia', 'giá vnd', 'gia vnd', 'vnd'] },
                { key: 'MaterialComposition', label: 'Thành phần', aliases: ['thành phần', 'thanh phan', 'materialcomposition'] },
                { key: 'Specifications', label: 'Quy cách', aliases: ['quy cách', 'quy cach', 'specifications'] },
                { key: 'FunctionUse', label: 'Công dụng', aliases: ['công dụng', 'cong dung', 'functionuse'] },
                { key: 'ManufacturerName', label: 'Nhà sản xuất', aliases: ['nhà sản xuất', 'nha san xuat', 'manufacturername'] },
                { key: 'ExternalDimension', label: 'Kích thước', aliases: ['kích thước', 'kich thuoc', 'externaldimension'] },
                { key: 'CountryOfOrigin', label: 'Xuất xứ', aliases: ['xuất xứ', 'xuat xu', 'countryoforigin'] },
                { key: 'SerialNumber', label: 'Số seri', aliases: ['số seri', 'so seri', 'serialnumber'] },
                { key: 'NetWeightPerPiece', label: 'NET.W/PCS', number: true, aliases: ['net.w/pcs', 'net w/pcs', 'netweightperpiece'] }
            ],
            log_headers: [
                { text: 'Người dùng', value: 'user_name', width: '150px' },
                { text: 'Thời gian', value: 'created_at', width: '180px' },
                { text: 'Nội dung', value: 'content' },
            ],
        };
    },
    computed: {
        ...mapState({
            order: state => state.cnOrder.selected,
            products: state => state.cnOrder.products,
            waybills: state => state.cnOrder.waybills,
            collections: state => state.cnOrder.collections,
            loading: state => state.cnOrder.loading,
        }),
        ...mapGetters('cnOrder', ['transportFees', 'trustFees', 'serviceFees', 'taxFees']),
        adminRole() {
            return this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Admin;
        },
        accountantRole() {
            return this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Accountant;
        },
        showCost() {
            return this.adminRole || this.accountantRole;
        },
        feeHeaders() {
            const h = [
                { text: 'Dịch vụ', value: 'ServiceName' },
            ];
            if (this.showCost) h.push({ text: 'Giá gốc', value: 'CostPrice', align: 'right' });
            h.push(
                { text: 'Giá bán', value: 'SellPrice', align: 'right' },
                { text: 'Đơn vị', value: 'Unit' },
                { text: 'SL', value: 'Quantity', align: 'right' },
                { text: 'Thành tiền', value: 'Amount', align: 'right' },
                { text: '', sortable: false, width: 80 }
            );
            return h;
        },
        taxHeaders() {
            return [
                { text: 'Loại thuế', value: 'ServiceName' },
                { text: '% Thuế', value: 'TaxRate', align: 'right' },
                { text: 'Giá trị', value: 'Amount', align: 'right' },
                { text: 'Tiền thuế', value: 'TaxAmount', align: 'right' },
                { text: '', sortable: false, width: 80 }
            ];
        },
        totalWaybillPacks() {
            return (this.waybills || []).reduce((s, w) => s + (w.PackageCount || 0), 0);
        },
        totalWaybillWeight() {
            return (this.waybills || []).reduce((s, w) => s + (parseFloat(w.TotalWeight) || 0), 0).toFixed(2);
        },
        totalWaybillVolume() {
            return (this.waybills || []).reduce((s, w) => s + (parseFloat(w.TotalVolume) || 0), 0).toFixed(4);
        },
        totalWaybillKGM() {
            const w = parseFloat(this.totalWaybillWeight);
            const v = parseFloat(this.totalWaybillVolume);
            return v > 0 ? (w / v).toFixed(2) : '0';
        },
        displayWaybills() {
            const list = [...(this.waybills || [])];
            if (this.editingWaybillId === 'new') {
                list.push({
                    ID: 'new',
                    WaybillCode: '',
                    Length: 0,
                    Width: 0,
                    Height: 0,
                    PackageCount: 1,
                    TotalWeight: 0,
                    TotalVolume: 0,
                    KGM: 0,
                    Note: ''
                });
            }
            return list;
        },
        routeList() {
            return (this.serviceList || []).filter(s => s.ServiceGroup === 'VanChuyen');
        },
        orderId() {
            return this.$route.params.Pid;
        },
        canMarkCustomsReady() {
            return !!this.order && !!this.order.ID && Number(this.order.Status) === configs.CN_ORDER_STATUS.CHO_KHAI_BAO;
        },
        trustFeeRows() {
            return (this.products || []).map(p => {
                const qty = parseFloat(p.Quantity) || 0
                const unitPrice = parseFloat(p.PriceVND) || 0
                const rate = parseFloat(this.trustFeeRates[p.ID]) || 0
                const giaTriHang = qty * unitPrice
                const thanhTien = giaTriHang * rate / 100
                const phaiThu = Math.max(thanhTien, thanhTien > 0 ? 500000 : 0)
                return { ...p, giaTriHang, thanhTien, phaiThu }
            })
        },
        totalTrustFeePayable() {
            return (this.trustFeeRows || []).reduce((s, r) => s + r.phaiThu, 0)
        },
        totalPriceB1() {
            return (this.products || []).reduce((s, p) => s + this.calcPriceValues(p).B1, 0)
        },
        totalPriceS() {
            return (this.products || []).reduce((s, p) => s + this.calcPriceValues(p).S, 0)
        },
        totalPriceG1() {
            return (this.products || []).reduce((s, p) => s + this.calcPriceValues(p).G1, 0)
        },
        totalCollections() {
            return (this.collections || []).reduce((s, c) => s + (parseFloat(c.Amount) || 0), 0)
        },
        debitItems() {
            const items = [];
            // Transport fees
            const tf = this.transportFees || [];
            if (tf.length > 0) {
                const total = tf.reduce((s, f) => s + (parseFloat(f.Amount) || 0), 0);
                if (total > 0) items.push({ Amount: total, Source: 'cước vc' });
            }
            // Trust fee
            if (this.totalTrustFeePayable > 0) {
                items.push({ Amount: this.totalTrustFeePayable, Source: 'phí ủy thác' });
            }
            // Service fees
            const sf = this.serviceFees || [];
            sf.forEach(f => {
                const amt = parseFloat(f.Amount) || 0;
                if (amt > 0) items.push({ Amount: amt, Source: f.ServiceName });
            });
            // Tax fees
            const taxf = this.taxFees || [];
            taxf.forEach(f => {
                const amt = parseFloat(f.TaxAmount) || 0;
                if (amt > 0) items.push({ Amount: amt, Source: f.ServiceName });
            });
            return items;
        },
        creditItems() {
            return (this.collections || []).map(c => ({
                Amount: parseFloat(c.Amount) || 0,
                Source: c.Note
            }));
        },
        totalDebit() {
            return this.debitItems.reduce((s, item) => s + item.Amount, 0);
        },
        totalCredit() {
            return this.creditItems.reduce((s, item) => s + item.Amount, 0);
        },
        balance() {
            return this.totalCredit - this.totalDebit;
        },
        balanceRows() {
            if (!this.order || !this.order.financial_balance) return [];
            const dLen = (this.order.financial_balance.debit_items || []).length;
            const cLen = (this.order.financial_balance.credit_items || []).length;
            return Array.from({ length: Math.max(dLen, cLen) });
        },
        isOrderLocked() {
            return this.order && parseInt(this.order.Status || 0) >= 6;
        }
    },
    methods: {
        getStatusColor(status) {
            const found = this.statusList.find(s => s.value === status);
            return found ? found.color : '#808080';
        },
        getStatusName(status) {
            const found = this.statusList.find(s => s.value === status);
            return found ? found.name : 'N/A';
        },
        onCustomerChange(customerId) {
            const cust = this.customers.find(c => c.ID === customerId);
            if (cust) {
                this.order.CustomerName = cust.FullName;
                const customerSaleId = cust.SaleID || cust.saleId || cust.saleID;
                if (customerSaleId) {
                    this.order.SaleID = customerSaleId;
                }
            } else {
                this.order.CustomerName = '';
            }
        },
        onRouteChange(code) {
            const route = this.routeList.find(r => r.ServiceCode === code);
            if (route) {
                this.order.RouteName = route.ServiceName;
            } else {
                this.order.RouteName = '';
            }
        },
        loadCustomers() {
            const token = this.$store.state.authentication.token;
            const req = { headers: { "Authorization": `Bearer ${token}` } };
            this.$http.get(`${configs.API}/api/customers/`, req).then(res => {
                if (res.data && res.data.data) {
                    this.customers = res.data.data.map(c => ({
                        ...c,
                        display: `${c.Username} - ${c.FullName}`
                    }));
                }
            });
        },
        loadStaff() {
            const token = this.$store.state.authentication.token;
            const req = { headers: { "Authorization": `Bearer ${token}` } };
            this.$http.get(`${configs.API}/api/users/`, req).then(res => {
                if (res.data && res.data.data) {
                    this.staffList = res.data.data.filter(u => u.RoleID == configs.ROLE_TYPE.Sale || u.RoleID == configs.ROLE_TYPE.Admin);
                }
            });
        },
        loadServices() {
            const token = this.$store.state.authentication.token;
            const req = { headers: { "Authorization": `Bearer ${token}` } };
            this.$http.get(`${configs.API}/api/cn-services/`, req).then(res => {
                if (res.data && res.data.data) {
                    this.serviceList = res.data.data;
                }
            });
        },
        loadOrder() {
            if (this.orderId && this.orderId != 0) {
                this.$store.dispatch('cnOrder/getFullDetail', this.orderId).then(() => {
                    this.getSystemLogs();
                });
            } else {
                this.$store.commit('cnOrder/setDetail', {
                    ID: 0, Status: 0, TransportType: 'Trucking',
                    TotalTransportFee: 0, TotalTrustFee: 0, TotalServiceFee: 0,
                    TotalTaxFee: 0, TotalAmount: 0
                });
                this.$store.commit('cnOrder/setProducts', []);
                this.$store.commit('cnOrder/setWaybills', []);
                this.$store.commit('cnOrder/setFees', []);
                this.systemLogs = [];
            }
        },
        async getSystemLogs() {
            if (!this.orderId || this.orderId == 0) {
                this.systemLogs = [];
                return;
            }

            this.loadingLogs = true;
            try {
                const param = {
                    pagination: { page: 1, itemsPerPage: 100, sortBy: ['id'], sortDesc: [true] },
                    filter: [
                        { column: 'function_name', value: 'Đơn hàng chính ngạch', condition: 's_equal' },
                        { column: 'object_id', value: this.orderId, condition: 'equal' }
                    ]
                };

                await this.$store.dispatch("systemLog/getList", param);
                this.systemLogs = this.$store.state.systemLog.all.data || [];
            } catch (err) {
                console.error('Error loading system logs:', err);
                this.systemLogs = [];
            } finally {
                this.loadingLogs = false;
            }
        },
        saveOrder() {
            if (this.isOrderLocked) {
                sendErrorNotice('Đơn hàng đã được khóa thanh toán, không thể chỉnh sửa.');
                return;
            }
            if (!this.order.CustomerID || !this.order.TransportType || !this.order.RouteServiceCode) {
                sendErrorNotice('Vui lòng nhập đủ thông tin chung bắt buộc: Khách hàng, Hình thức VC, Tuyến đường.');
                return;
            }
            const data = { ...this.order };
            delete data.products;
            delete data.waybills;
            delete data.fees;
            delete data.CustomerFullName;
            delete data.CustomerCode;

            if (this.order.ID && this.order.ID != 0) {
                this.$store.dispatch('cnOrder/setDetail', {
                    data: data,
                    id: this.order.ID
                }).then(() => {
                    this.getSystemLogs();
                });
            } else {
                this.$store.dispatch('cnOrder/setDetail', {
                    data: data,
                    id: 0
                }).then((created) => {
                    const newId = (created && created.ID) || (created && created.data && created.data.ID);
                    if (!newId) {
                        sendErrorNotice('Không lấy được ID đơn hàng vừa tạo. Vui lòng tải lại danh sách và thử lại.');
                        return;
                    }
                    this.$router.replace({ name: 'CnOrderDetail', params: { Pid: newId } });
                    this.$store.dispatch('cnOrder/getFullDetail', newId).then(() => {
                        this.getSystemLogs();
                    });
                });
            }
        },
        markCustomsReady() {
            if (!this.canMarkCustomsReady) {
                sendErrorNotice('Chỉ đơn hàng ở trạng thái Chờ khai báo mới được chuyển sang Chờ thông quan.');
                return;
            }
            if (!confirm('Xác nhận chuyển đơn hàng sang trạng thái Chờ thông quan?')) {
                return;
            }
            this.$store.dispatch('cnOrder/markCustomsReady', this.order.ID).then(() => {
                this.getSystemLogs();
            });
        },
        // ===== Product CRUD =====
        openProductDialog(item) {
            if (!this.order.ID && !item) {
                sendErrorNotice('Vui lòng lưu đơn hàng trước khi thêm hàng hóa.');
                return;
            }
            this.editingProduct = item ? { ...item } : { OrderID: this.orderId };
            this.productImageFile = null;
            this.nameplateImageFile = null;
            this.dialogProduct = true;
        },
        async onProductImageChange(file) {
            if (!file) return;
            const validation = fileUploadService.validateFile(file, ['jpg', 'jpeg', 'png', 'gif'], 5);
            if (!validation.valid) {
                sendErrorNotice(validation.message);
                this.productImageFile = null;
                return;
            }
            this.uploadingProductImage = true;
            const result = await fileUploadService.uploadSingleFile(file, 'product');
            if (result.success) {
                this.$set(this.editingProduct, 'ProductImage', result.data.url);
            } else {
                sendErrorNotice(result.message);
            }
            this.uploadingProductImage = false;
        },
        async onNameplateImageChange(file) {
            if (!file) return;
            const validation = fileUploadService.validateFile(file, ['jpg', 'jpeg', 'png', 'gif'], 5);
            if (!validation.valid) {
                sendErrorNotice(validation.message);
                this.nameplateImageFile = null;
                return;
            }
            this.uploadingNameplateImage = true;
            const result = await fileUploadService.uploadSingleFile(file, 'nameplate');
            if (result.success) {
                this.$set(this.editingProduct, 'NameplateImage', result.data.url);
            } else {
                sendErrorNotice(result.message);
            }
            this.uploadingNameplateImage = false;
        },
        saveProduct() {
            if (this.isOrderLocked) return;
            if (!this.editingProduct.ProductNameVN || !this.editingProduct.ProductNameEN || !this.editingProduct.HsCode || !this.editingProduct.Unit || !this.editingProduct.Quantity || this.editingProduct.Quantity <= 0) {
                sendErrorNotice('Vui lòng nhập đủ thông tin bắt buộc của hàng hóa.');
                return;
            }
            if (this.editingProduct.ID) {
                this.$store.dispatch('cnOrder/editProduct', {
                    orderId: this.orderId,
                    productId: this.editingProduct.ID,
                    data: this.editingProduct
                }).then(() => {
                    this.getSystemLogs();
                });
            } else {
                this.$store.dispatch('cnOrder/addProduct', {
                    orderId: this.orderId,
                    data: this.editingProduct
                }).then(() => {
                    this.getSystemLogs();
                });
            }
            this.dialogProduct = false;
        },
        exportProducts() {
            this.$store.dispatch('cnOrder/exportProducts');
        },
        removeProduct(productId) {
            if (this.isOrderLocked) return;
            if (confirm('Xóa hàng hóa này?')) {
                this.$store.dispatch('cnOrder/removeProduct', {
                    orderId: this.orderId,
                    productId: productId
                }).then(() => {
                    this.getSystemLogs();
                });
            }
        },
        updateTrustFeeRate(row) {
            if (this.isOrderLocked) return;
            const rate = parseFloat(this.trustFeeRates[row.ID]) || 0;
            this.$store.dispatch('cnOrder/editProduct', {
                orderId: this.orderId,
                productId: row.ID,
                data: { TrustFeeRate: rate }
            }).then(() => {
                this.getSystemLogs();
            }).catch(() => {
                this.loadOrder();
            });
        },
        updateProductPrice(item) {
            if (this.isOrderLocked) return;
            const { V1 } = this.calcPriceValues(item);
            item.PriceC_VND = Math.round(V1);
            this.$store.dispatch('cnOrder/editProduct', {
                orderId: this.orderId,
                productId: item.ID,
                data: {
                    ImportTaxRate: item.ImportTaxRate,
                    ExchangeRateUSD: item.ExchangeRateUSD,
                    PriceC_VND: item.PriceC_VND,
                    VatRate: item.VatRate
                }
            }).then(() => {
                this.getSystemLogs();
            }).catch(() => {
                this.loadOrder();
            });
        },
        updateInvoicePrice(item) {
            if (this.isOrderLocked) return;
            const price = parseFloat(item.InvoiceUnitPriceVND) || 0;
            this.$store.dispatch('cnOrder/editProduct', {
                orderId: this.orderId,
                productId: item.ID,
                data: { InvoiceUnitPriceVND: price }
            }).then(() => {
                this.getSystemLogs();
            }).catch(() => {
                this.loadOrder();
            });
        },
        calcPriceValues(item) {
            const M = parseFloat(item.Quantity) || 0;
            const unitUsd = parseFloat(item.PriceVND) || 0;
            const rate = parseFloat(item.ExchangeRateUSD) || 0;
            const r1 = parseFloat(item.ImportTaxRate) || 0;
            const r2 = parseFloat(item.VatRate) || 0;
            const A = unitUsd * M;
            const B1 = A * rate;
            const V1 = M > 0 ? B1 / M : 0;
            const S = B1 * r1 / 100;
            const G1 = B1 * r2 / 100;
            return { M, unitUsd, A, B1, V1, S, G1 };
        },
        // ===== Waybill CRUD =====
        addNewWaybillRow() {
            if (this.isOrderLocked) return;
            if (!this.order.ID || this.order.ID == 0) {
                sendErrorNotice('Vui lòng lưu đơn hàng trước khi thêm vận đơn.');
                return;
            }
            if (this.editingWaybillId) {
                sendErrorNotice('Vui lòng hoàn tất hoặc hủy phiên chỉnh sửa hiện tại.');
                return;
            }
            this.editingWaybillId = 'new';
            this.localWaybill = {
                WaybillCode: '',
                Length: 0,
                Width: 0,
                Height: 0,
                PackageCount: 1,
                TotalWeight: 0,
                TotalVolume: 0,
                KGM: 0,
                Note: ''
            };
        },
        editWaybillRow(item) {
            if (this.isOrderLocked) return;
            if (this.editingWaybillId) {
                sendErrorNotice('Vui lòng hoàn tất hoặc hủy phiên chỉnh sửa hiện tại.');
                return;
            }
            this.editingWaybillId = item.ID;
            this.localWaybill = { ...item };
        },
        calcLocalWaybillVolume() {
            const l = parseFloat(this.localWaybill.Length) || 0;
            const w = parseFloat(this.localWaybill.Width) || 0;
            const h = parseFloat(this.localWaybill.Height) || 0;
            const p = parseInt(this.localWaybill.PackageCount) || 0;
            const tw = parseFloat(this.localWaybill.TotalWeight) || 0;
            const vol = l * w * h * p / 1000000;
            this.localWaybill.TotalVolume = vol.toFixed(6);
            this.localWaybill.KGM = vol > 0 ? (tw / vol).toFixed(2) : '0';
        },
        saveWaybillRow() {
            if (this.isOrderLocked) return;
            if (!this.localWaybill.WaybillCode) {
                sendErrorNotice('Mã vận đơn là bắt buộc.');
                return;
            }
            const data = { ...this.localWaybill };
            if (this.editingWaybillId === 'new') {
                this.$store.dispatch('cnOrder/addWaybill', {
                    orderId: this.orderId,
                    data: data
                }).then(() => {
                    this.cancelWaybillEdit();
                    this.getSystemLogs();
                });
            } else {
                this.$store.dispatch('cnOrder/editWaybill', {
                    orderId: this.orderId,
                    waybillId: this.editingWaybillId,
                    data: data
                }).then(() => {
                    this.cancelWaybillEdit();
                    this.getSystemLogs();
                });
            }
        },
        cancelWaybillEdit() {
            this.editingWaybillId = null;
            this.localWaybill = {};
        },
        removeWaybill(waybillId) {
            if (this.isOrderLocked) return;
            if (confirm('Xóa vận đơn này?')) {
                this.$store.dispatch('cnOrder/removeWaybill', {
                    orderId: this.orderId,
                    waybillId: waybillId
                }).then(() => {
                    this.getSystemLogs();
                });
            }
        },
        // ===== Fee CRUD =====
        openFeeDialog(item) {
            if (!this.order.ID && !item) {
                sendErrorNotice('Vui lòng lưu đơn hàng trước khi thêm phí.');
                return;
            }
            this.editingFee = item ? { ...item } : { OrderID: this.orderId, FeeGroup: 'TRANSPORT' };
            this.selectedService = null;
            this.dialogFee = true;
        },
        onServiceSelect(service) {
            if (service) {
                this.editingFee.ServiceName = service.ServiceName;
                this.editingFee.ServiceCode = service.ServiceCode;
                this.editingFee.SellPrice = service.Price;
                this.editingFee.Unit = service.Unit;
                this.editingFee.ServiceGroupName = service.ServiceGroup;
            }
        },
        saveFee() {
            if (this.isOrderLocked) return;
            if (!this.editingFee.FeeGroup || !this.editingFee.ServiceName || !this.editingFee.Quantity || this.editingFee.Quantity <= 0) {
                sendErrorNotice('Vui lòng nhập đủ thông tin bắt buộc của phí.');
                return;
            }
            if (this.editingFee.ID) {
                this.$store.dispatch('cnOrder/editFee', {
                    orderId: this.orderId,
                    feeId: this.editingFee.ID,
                    data: this.editingFee
                }).then(() => {
                    this.getSystemLogs();
                });
            } else {
                this.$store.dispatch('cnOrder/addFee', {
                    orderId: this.orderId,
                    data: this.editingFee
                }).then(() => {
                    this.getSystemLogs();
                });
            }
            this.dialogFee = false;
        },
        removeFee(feeId) {
            if (this.isOrderLocked) return;
            if (confirm('Xóa phí này?')) {
                this.$store.dispatch('cnOrder/removeFee', {
                    orderId: this.orderId,
                    feeId: feeId
                }).then(() => {
                    this.getSystemLogs();
                });
            }
        },
        // ===== Collection CRUD =====
        openCollectionDialog(item) {
            if (!this.order.ID && !item) {
                sendErrorNotice('Vui lòng lưu đơn hàng trước khi thêm khoản thu.');
                return;
            }
            if (item) {
                this.editingCollection = { ...item };
            } else {
                const user = this.$store.state.authentication.user || {};
                this.editingCollection = { 
                    OrderID: this.orderId, 
                    ReceiverName: user.fullname || user.username || ''
                };
            }
            this.dialogCollection = true;
        },
        saveCollection() {
            if (this.isOrderLocked) return;
            if (!this.editingCollection.Note || !this.editingCollection.Amount || this.editingCollection.Amount <= 0) {
                sendErrorNotice('Vui lòng nhập ghi chú và số tiền > 0.');
                return;
            }
            if (this.editingCollection.ID) {
                this.$store.dispatch('cnOrder/editCollection', {
                    orderId: this.orderId,
                    collectionId: this.editingCollection.ID,
                    data: this.editingCollection
                }).then(() => {
                    this.getSystemLogs();
                });
            } else {
                this.$store.dispatch('cnOrder/addCollection', {
                    orderId: this.orderId,
                    data: this.editingCollection
                }).then(() => {
                    this.getSystemLogs();
                });
            }
            this.dialogCollection = false;
        },
        removeCollection(collectionId) {
            if (this.isOrderLocked) return;
            if (confirm('Xóa khoản thu này?')) {
                this.$store.dispatch('cnOrder/removeCollection', {
                    orderId: this.orderId,
                    collectionId: collectionId
                }).then(() => {
                    sendSuccessNotice(this, 'Xóa thành công');
                    this.getSystemLogs();
                });
            }
        },
        // ===== Import Excel =====
        openImportDialog() {
            this.dialogImport = true;
            this.mappingAutoApplied = false;
            this.importForm.file = null;
            this.excelHeaders = [];
            this.excelRows = [];
            this.currentHeaderSignature = '';
            this.columnMapping = {};
        },
        closeImportDialog() {
            this.dialogImport = false;
            this.dialogColumnMapping = false;
        },
        async onImportFileChange(file) {
            if (!file) {
                this.excelHeaders = [];
                this.excelRows = [];
                this.currentHeaderSignature = '';
                this.columnMapping = {};
                this.mappingAutoApplied = false;
                return;
            }
            await this.parseExcelFile(file);
        },
        parseExcelFile(file) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const data = new Uint8Array(e.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });
                        const firstSheet = workbook.SheetNames[0];
                        if (!firstSheet) { sendErrorNotice(this, 'File Excel không có sheet nào.'); resolve(); return; }
                        const worksheet = workbook.Sheets[firstSheet];
                        const raw = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
                        if (!raw || raw.length < 2) { sendErrorNotice(this, 'File Excel không có dữ liệu.'); resolve(); return; }
                        const headers = (raw[0] || []).map((h, idx) => {
                            const text = String(h || '').trim();
                            return text || `COL_${idx + 1}`;
                        });
                        const rows = raw.slice(1).filter(r => Array.isArray(r) && r.some(v => String(v || '').trim() !== ''));
                        const rowsAsObject = rows.map(r => {
                            const rowObj = {};
                            headers.forEach((h, i) => { rowObj[h] = r[i] !== undefined ? r[i] : ''; });
                            return rowObj;
                        });
                        this.excelHeaders = headers;
                        this.excelRows = rowsAsObject;
                        this.currentHeaderSignature = this.buildHeaderSignature(headers);
                        const guessed = this.guessMapping(headers);
                        const saved = this.getSavedMappingProfile();
                        if (saved && saved.signature === this.currentHeaderSignature && this.isMappingUsable(saved.mapping)) {
                            this.columnMapping = { ...saved.mapping };
                            this.mappingAutoApplied = true;
                            this.dialogColumnMapping = false;
                        } else {
                            this.columnMapping = guessed;
                            this.mappingAutoApplied = false;
                        }
                    } catch (err) {
                        sendErrorNotice(this, 'Không đọc được file Excel. Vui lòng kiểm tra lại định dạng file.');
                    }
                    resolve();
                };
                reader.readAsArrayBuffer(file);
            });
        },
        openMappingDialog() {
            if (!this.excelHeaders.length) { sendErrorNotice(this, 'Vui lòng chọn file Excel trước.'); return; }
            this.dialogColumnMapping = true;
        },
        confirmColumnMapping() {
            const missing = this.mappingFields.filter(f => f.required).filter(f => !this.columnMapping[f.key]);
            if (missing.length > 0) { sendErrorNotice(this, `Thiếu map cột bắt buộc: ${missing.map(m => m.label).join(', ')}`); return; }
            this.saveMappingProfile();
            this.mappingAutoApplied = true;
            this.dialogColumnMapping = false;
            sendSuccessNotice(this, 'Đã lưu mapping cột dữ liệu.');
        },
        normalizeHeader(value) {
            return String(value || '').trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
        },
        buildHeaderSignature(headers) {
            return (headers || []).map(h => this.normalizeHeader(h)).join('|');
        },
        guessMapping(headers) {
            const mapping = {};
            const normalizedHeaderMap = {};
            headers.forEach(h => { normalizedHeaderMap[this.normalizeHeader(h)] = h; });
            this.mappingFields.forEach(field => {
                const candidates = [field.label, field.key, ...(field.aliases || [])].map(a => this.normalizeHeader(a));
                let matched = null;
                for (let i = 0; i < candidates.length; i++) {
                    const c = candidates[i];
                    const exact = Object.keys(normalizedHeaderMap).find(k => k === c);
                    if (exact) { matched = normalizedHeaderMap[exact]; break; }
                    const partial = Object.keys(normalizedHeaderMap).find(k => k.includes(c) || c.includes(k));
                    if (partial) { matched = normalizedHeaderMap[partial]; break; }
                }
                if (matched) mapping[field.key] = matched;
            });
            return mapping;
        },
        getSavedMappingProfile() {
            try { const raw = localStorage.getItem(IMPORT_MAPPING_STORAGE_KEY); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
        },
        saveMappingProfile() {
            const payload = { signature: this.currentHeaderSignature, mapping: this.columnMapping, updatedAt: new Date().toISOString() };
            localStorage.setItem(IMPORT_MAPPING_STORAGE_KEY, JSON.stringify(payload));
        },
        isMappingUsable(mapping) {
            if (!mapping || !this.excelHeaders.length) return false;
            return Object.values(mapping).every(header => !header || this.excelHeaders.includes(header));
        },
        toNumber(value) {
            if (value === null || value === undefined || value === '') return 0;
            const parsed = parseFloat(String(value).replace(/,/g, '').trim());
            return Number.isNaN(parsed) ? 0 : parsed;
        },
        mapExcelRowToPayload(row) {
            const payload = {};
            this.mappingFields.forEach(field => {
                const source = this.columnMapping[field.key];
                if (!source) return;
                const rawValue = row[source];
                payload[field.key] = field.number ? this.toNumber(rawValue) : String(rawValue || '').trim();
            });
            return payload;
        },
        isEmptyMappedRow(payload) {
            return Object.keys(payload).length === 0 || Object.values(payload).every(v => v === '' || v === 0 || v === null || v === undefined);
        },
        async startImport() {
            if (!this.excelRows.length) { sendErrorNotice(this, 'Vui lòng chọn file Excel hợp lệ.'); return; }
            const missing = this.mappingFields.filter(f => f.required).filter(f => !this.columnMapping[f.key]);
            if (missing.length > 0) { this.dialogColumnMapping = true; sendErrorNotice(this, 'Vui lòng match đủ các cột bắt buộc trước khi import.'); return; }
            const products = [];
            const clientErrors = [];
            for (let i = 0; i < this.excelRows.length; i++) {
                const payload = this.mapExcelRowToPayload(this.excelRows[i]);
                if (this.isEmptyMappedRow(payload)) continue;
                if (!payload.ProductNameVN || this.toNumber(payload.Quantity) <= 0) { clientErrors.push(`Dòng ${i + 2}: Thiếu tên hàng hoặc số lượng không hợp lệ.`); continue; }
                payload.Quantity = this.toNumber(payload.Quantity);
                if (payload.PriceVND !== undefined) payload.PriceVND = this.toNumber(payload.PriceVND);
                if (payload.NetWeightPerPiece !== undefined) payload.NetWeightPerPiece = this.toNumber(payload.NetWeightPerPiece);
                products.push(payload);
            }
            if (products.length === 0 && clientErrors.length === 0) { sendErrorNotice(this, 'Không có dòng dữ liệu nào để import.'); return; }
            this.importing = true;
            try {
                const res = await ApiService.post(`/api/cn-orders/${this.orderId}/products/bulk-import/`, { products });
                const data = res && res.data && res.data.data ? res.data.data : {};
                const successCount = data.success_count || 0;
                const apiErrors = (data.errors || []).map(e => `Dòng ${e.row}: ${e.reason}`);
                const allErrors = [...clientErrors, ...apiErrors];
                if (successCount > 0) {
                    this.saveMappingProfile();
                    this.loadOrder();
                    sendSuccessNotice(this, `Import thành công ${successCount} sản phẩm.`);
                    this.getSystemLogs();
                }
                if (allErrors.length > 0) sendErrorNotice(this, `Có ${allErrors.length} dòng lỗi:\n${allErrors.slice(0, 5).join('\n')}`);
                if (successCount > 0 && allErrors.length === 0) this.closeImportDialog();
            } catch (e) {
                const reason = (e && e.body && e.body.reason) ? e.body.reason : (e && e.response && e.response.data && e.response.data.reason) ? e.response.data.reason : 'Lỗi kết nối hoặc server không phản hồi.';
                sendErrorNotice(this, `Import thất bại: ${reason}`);
            } finally {
                this.importing = false;
            }
        },
    },
    mounted() {
        this.loadCustomers();
        this.loadStaff();
        this.loadServices();
        this.loadOrder();
        this.$root.$on("REQUEST_SAVE_DATA", this.saveOrder);
        this.$root.$on("REQUEST_REFRESH_DATA", this.loadOrder);
    },
    beforeDestroy() {
        this.$root.$off("REQUEST_SAVE_DATA", this.saveOrder);
        this.$root.$off("REQUEST_REFRESH_DATA", this.loadOrder);
    },
    watch: {
        '$route.params.Pid'() {
            this.loadOrder();
        },
        products(newProducts) {
            if (!newProducts) return;
            const rates = {};
            newProducts.forEach(p => {
                rates[p.ID] = parseFloat(p.TrustFeeRate) || 0;
            });
            this.trustFeeRates = rates;
        }
    }
};
</script>

<style scoped>
.input-sm .v-input__control {
    min-height: 32px !important;
}

.input-sm input {
    font-size: 13px;
    padding: 4px 0;
}

/* ===== T-Account (Bảng cân đối tài chính) ===== */
.t-account-wrapper {
    overflow-x: auto;
    border-radius: 4px;
}

.t-account-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.t-account-table th,
.t-account-table td {
    border: 1px solid #bdbdbd;
    padding: 6px 10px;
}

/* Row 1 - tiêu đề chính: in đậm, căn giữa */
.t-account-header {
    text-align: center;
    font-weight: bold;
    font-size: 14px;
    background-color: #f5f5f5;
    padding: 10px;
}

/* Row 2 - Ghi nợ / Ghi có */
.t-account-side {
    text-align: center;
    font-weight: bold;
    font-size: 13px;
}
.t-account-debit  { background-color: #fff8f8; color: #c62828; }
.t-account-credit { background-color: #f8fff8; color: #2e7d32; }

/* Row 3 - sub-columns: Số tiền / Diễn giải */
.t-account-sub {
    background-color: #fafafa;
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
}

/* Data cells */
.t-account-amount {
    text-align: right;
    white-space: nowrap;
    width: 150px;
    min-height: 36px;
}
.t-account-desc {
    width: 25%;
    min-height: 36px;
}

/* Debit side cell background */
.t-account-debit-cell  { background-color: #fff8f8; }
/* Credit side cell background */
.t-account-credit-cell { background-color: #f8fff8; }

/* Tổng cộng row */
.t-account-total-row td {
    border-top: 2px solid #9e9e9e;
    padding: 7px 10px;
}
.t-account-total-amount { text-align: right; white-space: nowrap; }
.t-account-total-label  { font-size: 12px; }

/* Số tiền cân đối row */
.t-account-balance-row {
    text-align: right;
    padding: 8px 12px;
    background-color: #f5f5f5;
    border-top: 2px solid #9e9e9e;
    font-size: 14px;
}
</style>
